import QRCodeStyling from 'qr-code-styling';

// Portal branded QR — the portal mark centered in the code (Phase 12).
// Error-correction level H (30% recovery) + a small logo (~20%) keeps it scannable
// with the logo excised from the middle. Same-origin logo so canvas export isn't
// tainted. The transparent mark, not the app icon: a black tile in the middle of
// a code reads as damage. Docs: docs/plans/2026-08-ui-refresh/14-qr-codes-and-share-posters.md

const PORTAL_MARK = `${window.location.origin}/portal-mark-512.png`;

export function makeBrandedQr(value, size, opts = {}) {
  return new QRCodeStyling({
    width: size,
    height: size,
    type: 'canvas',
    // A stable literal, deliberately NOT window.location.origin: this ends up on
    // something printed, and a QR generated from a dev build or a preview host
    // would encode that host onto paper forever.
    data: value || 'https://heyportal.app',
    image: opts.logo === false ? undefined : PORTAL_MARK,
    margin: opts.margin ?? Math.round(size * 0.04),
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions: { color: opts.fg ?? '#000000', type: opts.dots ?? 'square' },
    backgroundOptions: { color: opts.bg ?? '#ffffff' },
    cornersSquareOptions: { type: 'square', color: opts.fg ?? '#000000' },
    cornersDotOptions: { type: 'square', color: opts.fg ?? '#000000' },
    // Keep the logo small so scanning never breaks; excise dots behind it.
    imageOptions: { imageSize: opts.logoSize ?? 0.2, margin: opts.logoMargin ?? 6, crossOrigin: 'anonymous', hideBackgroundDots: true },
  });
}

/** Branded-QR as a PNG Blob (for download / canvas embedding). */
export async function brandedQrBlob(value, size = 512, opts = {}) {
  const qr = makeBrandedQr(value, size, opts);
  const data = await qr.getRawData('png');
  // Browser returns a Blob; guard for other shapes.
  return data instanceof Blob ? data : new Blob([data], { type: 'image/png' });
}

/** Branded-QR as an <img> element, ready to drawImage() onto a canvas. */
export async function brandedQrImage(value, size = 512, opts = {}) {
  const blob = await brandedQrBlob(value, size, opts);
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('QR image failed'));
      img.src = url;
    });
  } finally {
    // Revoke after the image has loaded/drawn on next tick.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}
